import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { IMasterForm } from "./masterForms.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

// Action Types
export const FETCH_MASTER_FORM_LIST_PROGRESS =
  "FETCH_MASTER_FORM_LIST_PROGRESS";
export const FETCH_MASTER_FORM_LIST_SUCCESS = "FETCH_MASTER_FORM_LIST_SUCCESS";
export const FETCH_MASTER_FORM_LIST_FAILED = "FETCH_MASTER_FORM_LIST_FAILED";

export const FETCH_MASTER_FORM_PROGRESS = "FETCH_MASTER_FORM_PROGRESS";
export const FETCH_MASTER_FORM_SUCCESS = "FETCH_MASTER_FORM_SUCCESS";
export const FETCH_MASTER_FORM_FAILED = "FETCH_MASTER_FORM_FAILED";

export const CLEAR_MASTER_FORM_STATE = "CLEAR_MASTER_FORM_STATE";
export const CLEAR_MASTER_FORM = "CLEAR_MASTER_FORM";

// Action Creators
export const fetchMasterFormListProgress = () =>
  action(FETCH_MASTER_FORM_LIST_PROGRESS);
export const fetchMasterFormListSuccess = (
  data: IMasterForm[],
  totalRecords: number
) => action(FETCH_MASTER_FORM_LIST_SUCCESS, { data, totalRecords });
export const fetchMasterFormListFailed = () =>
  action(FETCH_MASTER_FORM_LIST_FAILED);

export const fetchMasterFormProgress = () => action(FETCH_MASTER_FORM_PROGRESS);
export const fetchMasterFormSuccess = (data: IMasterForm) =>
  action(FETCH_MASTER_FORM_SUCCESS, { data });
export const fetchMasterFormFailed = () => action(FETCH_MASTER_FORM_FAILED);

export const clearMasterFormState = () => action(CLEAR_MASTER_FORM_STATE);
export const clearMasterForm = () => action(CLEAR_MASTER_FORM);

// Async Actions
export const fetchMasterFormListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,

    status?: string,
    date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByName?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = "";

      if ((searchValue?.length ?? 0) > 0 || searchByName) {
        const advanceFilter = [...(searchValue || [])];

        if (searchByName) {
          advanceFilter.push({
            column: ["master_form_name"],
            value: searchByName,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      if (date) {
        searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      }

      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      const finalUrl =
        `/siteConfiguration/get-master-form?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}` +
        searchQuery;

      dispatch(fetchMasterFormListProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(finalUrl);
      const data: IMasterForm[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchMasterFormListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchMasterFormListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message || "Failed to fetch master forms",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchMasterFormAsync =
  (masterFormUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchMasterFormProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(
        `/siteConfiguration/get-master-form?master_form_uuid=${masterFormUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchMasterFormSuccess(data[0]));
      } else {
        dispatch(fetchMasterFormFailed());
      }
    } catch (err: any) {
      dispatch(fetchMasterFormFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message || "Failed to fetch master form",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertMasterFormAsync =
  (
    form: IMasterForm,
    onCallback: (isSuccess?: boolean, data?: IMasterForm) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(saveLoaderProgress());
    try {
      const {
        create_ts,
        insert_ts,
        created_by_uuid,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        comment,
        ...rest
      } = form;

      const payload = {
        ...rest,
      };

      const res = await api.post(
        "/siteConfiguration/upsert-master-form",
        payload
      );
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message: err?.response?.data?.message || "Failed to save master form",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };
